import { Button } from '@cottage/design-system/internal-tooling/ui'
import { HoverHelp } from '@/tableControls'
import type { FloorAnalysisResponse } from '@/services/projections'
import { formatCurrency, formatCurrencyWhole } from '../projectionsFormat'
import SegmentedToggle from './SegmentedToggle'
import { POLICY_CONTROL_WIDTH } from './policyLayout'

export type FloorAnalysisWindowMonths = 3 | 6 | 12

const BANK_STATEMENT_HELP =
  'Historical cash metrics to help size the floor. Stats exclude owner distributions using the QBO bank transactions already matched to TCG Dist #N journal entries; cash-out date is the statement posted date.'

const METRIC_HELP = {
  maxDrawdown:
    'Largest drop from month-end balance to the lowest daily balance in the statement window.',
  meanDrawdown: 'Average drawdown across months in the window.',
  p90Drawdown:
    '90th percentile drawdown — a worse-than-typical dip, but not the single worst month.',
  avgDailyOutflow:
    'Average daily cash outflows across the window. Owner distributions (QBO-matched) are excluded.',
  recommendedFloor:
    'Suggested cash floor: max drawdown plus cushion days × avg daily outflow.',
} as const

function parseStat(value: string | undefined | null): number | null {
  if (value == null) {
    return null
  }
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

export function recommendedFloorFromStats(
  stats: FloorAnalysisResponse['stats'] | undefined,
  cushionDays: number,
): number | null {
  if (!stats) {
    return null
  }
  const maxDrawdown = parseStat(stats.max_drawdown)
  const avgDailyOutflow = parseStat(stats.avg_daily_outflow)
  if (maxDrawdown == null || avgDailyOutflow == null) {
    return null
  }
  return maxDrawdown + avgDailyOutflow * cushionDays
}

type FloorAnalysisPanelProps = {
  data: FloorAnalysisResponse | null
  loading: boolean
  windowMonths: FloorAnalysisWindowMonths
  cushionDays: number
  canEdit: boolean
  onWindowMonthsChange: (months: FloorAnalysisWindowMonths) => void
  onCushionDaysChange: (days: number) => void
  onUseRecommendedFloor: (amount: number) => void
}

export default function FloorAnalysisPanel({
  data,
  loading,
  windowMonths,
  cushionDays,
  canEdit,
  onWindowMonthsChange,
  onCushionDaysChange,
  onUseRecommendedFloor,
}: FloorAnalysisPanelProps) {
  if (!data && !loading) {
    return null
  }

  const ready = data != null
  const maxDrawdown = ready ? Number(data.stats.max_drawdown) || 0 : 0
  const p90Drawdown = ready ? Number(data.stats.p90_drawdown) || 0 : 0
  const meanDrawdown = ready ? Number(data.stats.mean_drawdown) || 0 : 0
  const avgDailyOutflow = ready ? Number(data.stats.avg_daily_outflow) || 0 : 0
  const recommendedFloor = ready ? recommendedFloorFromStats(data.stats, cushionDays) : null
  const totalMonths = data ? data.months.length + data.missing_months.length : 0

  return (
    <div className="mt-2 space-y-3 rounded border border-gray-200 bg-white p-3">
      <p className="text-xs font-medium text-gray-700">
        <HoverHelp label="Bank statement reference" aria-label="Bank statement reference help">
          {BANK_STATEMENT_HELP}
        </HoverHelp>
      </p>
      {loading ? (
        <p className="text-xs text-gray-500">
          {ready
            ? 'Refreshing bank-statement stats from QuickBooks…'
            : 'Loading bank-statement stats from QuickBooks…'}
        </p>
      ) : null}
      {ready && data.missing_months.length > 0 ? (
        <p className="text-xs text-gray-600">
          {data.missing_months.length} of {totalMonths} months have no statement uploaded
        </p>
      ) : null}
      <div className="flex items-center justify-between gap-3">
        <span className="shrink-0 text-xs text-gray-600">Statement window</span>
        <div className={`shrink-0 ${POLICY_CONTROL_WIDTH}`}>
          <SegmentedToggle<FloorAnalysisWindowMonths>
            fullWidth
            value={windowMonths}
            options={[
              { value: 3, label: 'L3M' },
              { value: 6, label: 'L6M' },
              { value: 12, label: 'LTM' },
            ]}
            onChange={onWindowMonthsChange}
            ariaLabel="Bank statement analysis window"
          />
        </div>
      </div>
      <dl className="space-y-2 text-xs text-gray-700">
        <div className="flex items-center justify-between gap-3">
          <dt className="text-gray-500">
            <HoverHelp label="Max drawdown" aria-label="Max drawdown help">
              {METRIC_HELP.maxDrawdown}
            </HoverHelp>
          </dt>
          <dd className="ml-auto tabular-nums text-right text-gray-900">
            {ready ? formatCurrencyWhole(maxDrawdown) : '—'}
          </dd>
        </div>
        <div className="flex items-center justify-between gap-3">
          <dt className="text-gray-500">
            <HoverHelp label="Mean drawdown" aria-label="Mean drawdown help">
              {METRIC_HELP.meanDrawdown}
            </HoverHelp>
          </dt>
          <dd className="ml-auto tabular-nums text-right text-gray-900">
            {ready ? formatCurrencyWhole(meanDrawdown) : '—'}
          </dd>
        </div>
        <div className="flex items-center justify-between gap-3">
          <dt className="text-gray-500">
            <HoverHelp label="P90 drawdown" aria-label="P90 drawdown help">
              {METRIC_HELP.p90Drawdown}
            </HoverHelp>
          </dt>
          <dd className="ml-auto tabular-nums text-right text-gray-900">
            {ready ? formatCurrencyWhole(p90Drawdown) : '—'}
          </dd>
        </div>
        <div className="flex items-center justify-between gap-3">
          <dt className="text-gray-500">
            <HoverHelp label="Avg daily outflow" aria-label="Avg daily outflow help">
              {METRIC_HELP.avgDailyOutflow}
            </HoverHelp>
          </dt>
          <dd className="ml-auto tabular-nums text-right text-gray-900">
            {ready ? formatCurrency(avgDailyOutflow) : '—'}
          </dd>
        </div>
        <div className="flex items-center justify-between gap-3">
          <dt className="text-gray-500">
            <HoverHelp label="Recommended floor" aria-label="Recommended floor help">
              {METRIC_HELP.recommendedFloor}
            </HoverHelp>
          </dt>
          <dd className="ml-auto tabular-nums text-right text-gray-900">
            {!ready || recommendedFloor == null ? '—' : formatCurrencyWhole(recommendedFloor)}
          </dd>
        </div>
      </dl>
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs text-gray-600">
          <HoverHelp label="Cushion days" aria-label="Cushion days help">
            Extra days of average daily outflow added when recommending floor size from bank
            statements.
          </HoverHelp>
        </span>
        <div
          className={`flex h-7 items-stretch overflow-hidden rounded-md border border-gray-300 bg-white ${POLICY_CONTROL_WIDTH}`}
        >
          <button
            type="button"
            className="flex w-7 shrink-0 items-center justify-center border-r border-gray-300 text-xs text-gray-700 hover:bg-gray-50"
            aria-label="Decrease cushion days"
            onClick={() => onCushionDaysChange(Math.max(0, cushionDays - 1))}
          >
            −
          </button>
          <span className="flex flex-1 items-center justify-center text-xs tabular-nums text-gray-900">
            {cushionDays}
          </span>
          <button
            type="button"
            className="flex w-7 shrink-0 items-center justify-center border-l border-gray-300 text-xs text-gray-700 hover:bg-gray-50"
            aria-label="Increase cushion days"
            onClick={() => onCushionDaysChange(cushionDays + 1)}
          >
            +
          </button>
        </div>
      </div>
      <Button
        variant="primary"
        gradient
        size="sm"
        className="w-full"
        disabled={loading || recommendedFloor == null || !canEdit}
        onClick={() => {
          if (recommendedFloor != null) {
            onUseRecommendedFloor(recommendedFloor)
          }
        }}
      >
        Use recommended floor
      </Button>
    </div>
  )
}
